import { SUB_CATEGORIES, TICKET_TYPES } from './ticket.constants';

export const TOOL_LOGO_MAP: Record<string, string> = {
  'claude code': 'assets/tool-logos/claude-icon.svg',
  'claude': 'assets/tool-logos/claude-icon.svg',
  'cursor': 'assets/tool-logos/cursor-ai-code-icon.svg',
  'codex': 'assets/tool-logos/codex-color.svg',
  'windsurf': 'assets/tool-logos/windsurf-icon.svg',
  'g-suite': 'assets/tool-logos/gsuite-icon.svg',
  'gsuite': 'assets/tool-logos/gsuite-icon.svg',
  'google workspace': 'assets/tool-logos/gsuite-icon.svg',
  'google suite': 'assets/tool-logos/gsuite-icon.svg',
  'github': 'assets/tool-logos/github-icon.svg',
  'proofhub': 'assets/tool-logos/proofhub-icon.svg',
  'firewall': 'assets/tool-logos/firewall-icon.svg',
};

export const TOOL_ACCESS_CATEGORY_NAMES = {
  NETWORK_AND_INFRASTRUCTURE: 'Network & Infrastructure',
};

const TOOL_ACCESS_SUB_CATEGORIES_WITH_DETAIL = ['High Speed Internet', 'Website Unblocking', 'GitHub', 'ProofHub'];

export const GITHUB_ACCESS_TYPE = {
  ORG: 'Coditas LLP',
  REPO: 'Repo Access',
  CREATE_REPO: 'Create Repository',
} as const;

export const GITHUB_ACCESS_TYPE_OPTIONS: string[] = [
  GITHUB_ACCESS_TYPE.ORG,
  GITHUB_ACCESS_TYPE.REPO,
  GITHUB_ACCESS_TYPE.CREATE_REPO,
];

export const GITHUB_ROLE_LABELS: string[] = ['Read', 'Triage', 'Write', 'Maintain', 'Admin'];

export const GITHUB_RESOURCE_TYPE = {
  ORG: 'ORG',
  REPO: 'REPO',
  CREATE_REPO: 'CREATE_REPO',
} as const;

export const GITHUB_VERIFICATION_FAILED_MESSAGE_FRAGMENT = "couldn't verify";

export const GITHUB_ORG_PERMISSION_LEVEL_LABEL = 'Member';

export const GITHUB_REPO_NAME_PATTERN = /^[A-Za-z0-9._-]+$/;

export const GITHUB_USERNAME_SUFFIX = '-coditas';

export const GITHUB_USERNAME_PATTERN = /^[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*-coditas$/i;

export const GITHUB_REPO_NAME_HINT =
  'Only letters, numbers, periods (.), underscores (_), and hyphens (-) are allowed — no spaces or other special characters.';

export const githubUsernameSuffixHintText = (suffix: string): string =>
  `GitHub username must end with '${suffix}' (e.g., firstname-lastname${suffix}).`;

export const GITHUB_USERNAME_FINAL_NOTICE =
  'This username will be linked to your account when you submit the request and used for all your future GitHub access requests.';

export const githubUsernameInvalidSuffixMessage = (suffix: string): string =>
  `Your GitHub username must end with "${suffix}". Please rename your GitHub account and try again.`;

export const TOOL_ACCESS_PERMANENT_DURATION = 0;

export const TOOL_ACCESS_PERMANENT_EXPIRY_LABEL = 'No Expiry';

export const PROOFHUB_NORMAL_USER_ACCESS_TYPE_LABEL = 'Normal User';

export const PROOFHUB_PROJECT_SEARCH_PLACEHOLDER = 'Search projects...';

export const PROOFHUB_PROJECT_LOADING_TEXT = 'Loading projects...';

export const PROOFHUB_PROJECT_EMPTY_TEXT = 'No projects found.';

export const PROOFHUB_PROJECT_LOAD_ERROR_FALLBACK = 'Could not load projects. Please try again.';

export const PROOFHUB_DUPLICATE_PROJECT_REQUEST_MESSAGE =
  'You already have an active or pending request for access to this ProofHub project.';

export function composeToolDisplayName(
  subCategory: string | undefined,
  keywords: string | undefined,
): string {
  const trimmedKeywords = keywords?.trim();
  if (!subCategory) return trimmedKeywords || '—';
  if (subCategory === 'Other') return trimmedKeywords || subCategory;
  const hasSeparateDetail =
    TOOL_ACCESS_SUB_CATEGORIES_WITH_DETAIL.includes(subCategory) &&
    !!trimmedKeywords &&
    trimmedKeywords !== subCategory;
  return hasSeparateDetail ? `${subCategory} (${trimmedKeywords})` : trimmedKeywords || subCategory;
}

export function formatWebsiteValue(value: string | undefined): string {
  const trimmed = value?.trim();
  if (!trimmed) return trimmed ?? '';
  return trimmed
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '');
}

export function composeToolCardDisplayName(
  subCategory: string | undefined,
  keywords: string | undefined,
): string {
  const trimmedKeywords = keywords?.trim();
  if (subCategory === SUB_CATEGORIES.WEBSITE_UNBLOCKING) {
    return trimmedKeywords ? formatWebsiteValue(trimmedKeywords) : subCategory;
  }
  return composeToolDisplayName(subCategory, keywords);
}

export const TOOL_ACCESS_SORT_COLUMNS = {
  EMPLOYEE: 'EMPLOYEE',
  TOOL: 'TOOL',
  ACCESS_TYPE: 'ACCESS_TYPE',
  STATUS: 'STATUS',
  START_DATE: 'START_DATE',
  END_DATE: 'END_DATE',
  LAST_TICKET: 'LAST_TICKET',
  UPDATED_AT: 'UPDATED_AT',
};

export const TOOL_ACCESS_TYPES = new Set([
  TICKET_TYPES.TOOL_ACCESS_REQUEST,
  TICKET_TYPES.TOOL_ACCESS_RENEWAL,
  TICKET_TYPES.TOOL_ACCESS_REVOKE,
]);

export const TOOL_ACCESS_AUTO_RESOLUTION_PREFIXES = [
  'Access provisioned automatically on',
  'Access revoked automatically on',
];

export const TOOL_ACCESS_STATUS = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  EXPIRING_SOON: 'EXPIRING_SOON',
  EXPIRED: 'EXPIRED',
  RENEWAL_PENDING: 'RENEWAL_PENDING',
  REVOKE_PENDING: 'REVOKE_PENDING',
  REVOKED: 'REVOKED',
  DECLINED: 'DECLINED',
} as const;

export const TOOL_ACCESS_STATUS_LABELS: Record<string, string> = {
  [TOOL_ACCESS_STATUS.PENDING]: 'Pending',
  [TOOL_ACCESS_STATUS.ACTIVE]: 'Active',
  [TOOL_ACCESS_STATUS.EXPIRING_SOON]: 'Expiring Soon',
  [TOOL_ACCESS_STATUS.EXPIRED]: 'Expired',
  [TOOL_ACCESS_STATUS.RENEWAL_PENDING]: 'Renewal Pending',
  [TOOL_ACCESS_STATUS.REVOKE_PENDING]: 'Revoke Pending',
  [TOOL_ACCESS_STATUS.REVOKED]: 'Revoked',
  [TOOL_ACCESS_STATUS.DECLINED]: 'Declined',
};

export const isToolAccessUpgradePending = (status?: string | null, accessLevel?: string | null): boolean =>
  status === TOOL_ACCESS_STATUS.RENEWAL_PENDING && !!accessLevel;

export const toolAccessStatusLabel = (status?: string | null, accessLevel?: string | null): string =>
  isToolAccessUpgradePending(status, accessLevel)
    ? TOOL_ACCESS_STATUS_LABELS[TOOL_ACCESS_STATUS.PENDING]
    : TOOL_ACCESS_STATUS_LABELS[status ?? ''] ?? status ?? '';

export const REVOKE_REASONS = [
  'Project ended',
  'Tool no longer needed',
  'Switching to a different tool',
  'Other',
] as const;

export const TOOL_ACCESS_BLOCK_STATUSES: string[] = [
  TOOL_ACCESS_STATUS.ACTIVE,
  TOOL_ACCESS_STATUS.PENDING,
  TOOL_ACCESS_STATUS.RENEWAL_PENDING,
  TOOL_ACCESS_STATUS.EXPIRING_SOON,
  TOOL_ACCESS_STATUS.REVOKE_PENDING,
];

export const TOOL_ACCESS_PENDING_GROUP_STATUSES: string[] = [
  TOOL_ACCESS_STATUS.PENDING,
  TOOL_ACCESS_STATUS.RENEWAL_PENDING,
  TOOL_ACCESS_STATUS.REVOKE_PENDING,
];

export const TOOL_ACCESS_RENEWABLE_STATUSES: string[] = [
  TOOL_ACCESS_STATUS.ACTIVE,
  TOOL_ACCESS_STATUS.EXPIRING_SOON
];

export const TOOL_ACCESS_REVOKE_ACCESS_STATUSES: string[] = [
  TOOL_ACCESS_STATUS.ACTIVE,
  TOOL_ACCESS_STATUS.EXPIRING_SOON,
  TOOL_ACCESS_STATUS.EXPIRED,
  TOOL_ACCESS_STATUS.REVOKE_PENDING,
];

export const TOOL_ACCESS_TERMINAL_STATUSES: string[] = [
  TOOL_ACCESS_STATUS.EXPIRED,
  TOOL_ACCESS_STATUS.REVOKED,
  TOOL_ACCESS_STATUS.DECLINED,
];

export const TOOL_ACCESS_FILTERS = [
  { title: 'All', value: '' },
  { title: 'Active', value: TOOL_ACCESS_STATUS.ACTIVE },
  { title: 'Expiring Soon', value: TOOL_ACCESS_STATUS.EXPIRING_SOON },
  { title: 'Pending', value: 'PENDING_GROUP' },
  { title: 'Expired', value: TOOL_ACCESS_STATUS.EXPIRED },
  { title: 'Revoked', value: TOOL_ACCESS_STATUS.REVOKED },
  { title: 'Declined', value: TOOL_ACCESS_STATUS.DECLINED }
];

export const TOOL_ACCESS_RENEWAL_IN_PROGRESS = 'Renewal in progress';

export const TOOL_ACCESS_SECTION_TITLE = 'My Tool / Access';

export const TOOL_ACCESS_DEFAULT_PAGE_SIZE = '10';

export const TOOL_ACCESS_MAX_RENEWAL_DURATION = 120;

export const TOOL_ACCESS_DEFAULT_RENEWAL_DURATION = 30;

export const TOOL_ACCESS_RENEWAL_DURATION_OPTIONS = [30, 60, 90, 120] as const;

export type RenewalDurationSelection =
  (typeof TOOL_ACCESS_RENEWAL_DURATION_OPTIONS)[number];

export const TOOL_ACCESS_DURATION_EDITABLE_TICKET_TYPES: string[] = [
  TICKET_TYPES.TOOL_ACCESS_REQUEST,
  TICKET_TYPES.TOOL_ACCESS_RENEWAL,
];

export const TOOL_ACCESS_DURATION_EDITABLE_STATUSES: string[] = [
  TOOL_ACCESS_STATUS.PENDING,
  TOOL_ACCESS_STATUS.RENEWAL_PENDING,
];

export const TOOL_ACCESS_DURATION_EDIT_LABELS = {
  DURATION: 'Duration',
  REQUESTED_DURATION: 'Requested Duration',
  UPDATED_DURATION: 'Updated Duration',
  SELECT_DURATION: 'Select Duration',
  EDIT_DURATION: 'Edit duration',
  CANCEL: 'Cancel',
  ACCESS_DURATION: 'Access Duration',
};

export function buildDurationOptionLabel(days: number): string {
  return `${days} Days`;
}

export function buildGrantedDurationLabel(grantedDays: number, requestedDays: number | null): string {
  const granted = `${grantedDays} days`;
  return requestedDays && requestedDays !== grantedDays
    ? `${granted} (requested ${requestedDays} days)`
    : granted;
}

export function buildRenewalExpiryDate(currentExpiry: string, days: number, today: Date = new Date()): Date {
  const todayStart = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  const expiry = new Date(currentExpiry);
  const renewed = new Date(expiry > todayStart ? expiry : todayStart);
  renewed.setUTCDate(renewed.getUTCDate() + days);
  return renewed;
}

export const TOOL_ACCESS_RENEWAL_TICKET_TYPE = 'TOOL_ACCESS_RENEWAL';

export const TOOL_ACCESS_RENEWAL_TITLE = 'Renew Access';

export const TOOL_ACCESS_REVOKE_TICKET_TYPE = 'TOOL_ACCESS_REVOKE';

export const TOOL_ACCESS_REVOKE_TITLE = 'Revoke Access';

export const TOOL_ACCESS_EMPTY_FILTER_MESSAGES: Record<string, { title: string; subtitle: string }> = {
  '': {
    title: 'No tool / access records',
    subtitle: 'You do not have any tool / access records yet.',
  },
  [TOOL_ACCESS_STATUS.ACTIVE]: {
    title: 'No active tool / access',
    subtitle: 'You do not have any tools / access that are active right now.',
  },
  [TOOL_ACCESS_STATUS.EXPIRING_SOON]: {
    title: 'No expiring tool / access',
    subtitle: 'None of your tools / access are expiring soon.',
  },
  PENDING_GROUP: {
    title: 'No pending requests',
    subtitle: 'You do not have any tool / access requests awaiting approval.',
  },
  [TOOL_ACCESS_STATUS.EXPIRED]: {
    title: 'No expired tool / access',
    subtitle: 'You do not have any expired tool / access records.',
  },
  [TOOL_ACCESS_STATUS.REVOKED]: {
    title: 'No revoked tool / access',
    subtitle: 'You do not have any revoked tool / access records.',
  },
  [TOOL_ACCESS_STATUS.DECLINED]: {
    title: 'No declined tool / access',
    subtitle: 'You do not have any declined tool / access records.',
  }
};

export const TOOL_ACCESS_ADMIN_STATUS_CLASS_MAP: Record<string, string> = {
  [TOOL_ACCESS_STATUS.ACTIVE]: 's-active',
  [TOOL_ACCESS_STATUS.EXPIRING_SOON]: 's-expiring',
  [TOOL_ACCESS_STATUS.RENEWAL_PENDING]: 's-renewal',
  [TOOL_ACCESS_STATUS.REVOKE_PENDING]: 's-revoke',
  [TOOL_ACCESS_STATUS.PENDING]: 's-rpending',
  [TOOL_ACCESS_STATUS.EXPIRED]: 's-expired',
  [TOOL_ACCESS_STATUS.REVOKED]: 's-revoked',
  [TOOL_ACCESS_STATUS.DECLINED]: 's-declined',
};

export type ToolAccessAdminTab =
  | 'Active'
  | 'Pending'
  | 'Expired'
  | 'Revoked'
  | 'Declined'
  | 'All';

export const TOOL_ACCESS_ADMIN_DEFAULT_TAB: ToolAccessAdminTab = 'All';

export const TOOL_ACCESS_ADMIN_TAB_QUERY_MAP: Record<ToolAccessAdminTab, string> = {
  Active: 'ACTIVE',
  Pending: 'PENDING',
  Expired: 'EXPIRED',
  Revoked: 'REVOKED',
  Declined: 'DECLINED',
  All: 'ALL',
};

type ToolAccessAdminTabRecord = { status: string };

export const TOOL_ACCESS_ADMIN_TAB_FILTER: Record<
  ToolAccessAdminTab,
  (record: ToolAccessAdminTabRecord) => boolean
> = {
  Active: (record) =>
    record.status === TOOL_ACCESS_STATUS.ACTIVE ||
    record.status === TOOL_ACCESS_STATUS.EXPIRING_SOON,
  Pending: (record) =>
    record.status === TOOL_ACCESS_STATUS.RENEWAL_PENDING ||
    record.status === TOOL_ACCESS_STATUS.REVOKE_PENDING ||
    record.status === TOOL_ACCESS_STATUS.PENDING,
  Expired: (record) => record.status === TOOL_ACCESS_STATUS.EXPIRED,
  Revoked: (record) => record.status === TOOL_ACCESS_STATUS.REVOKED,
  Declined: (record) => record.status === TOOL_ACCESS_STATUS.DECLINED,
  All: () => true,
};

export const TOOL_ACCESS_ADMIN_EMPTY_TAB_TITLES: Record<ToolAccessAdminTab, string> = {
  Active: 'No active tool / access',
  Pending: 'No pending requests',
  Expired: 'No expired tool / access',
  Revoked: 'No revoked tool / access',
  Declined: 'No declined tool / access',
  All: 'No tool / access records found',
};

export const TOOL_ACCESS_ADMIN_EMPTY_TAB_SUBTITLES: Record<ToolAccessAdminTab, string> = {
  Active: 'There are no active tools / access records',
  Pending: 'There are no pending tool / access records.',
  Expired: 'There are no expired tool / access records.',
  Revoked: 'There are no revoked tool / access records.',
  Declined: 'There are no declined tool / access records.',
  All: 'There are no tool / access records to display.',
};

export const STATUS_CLASS_MAP: Record<string, string> = {
  [TOOL_ACCESS_STATUS.ACTIVE]:          's-active',
  [TOOL_ACCESS_STATUS.EXPIRING_SOON]:   's-expiring',
  [TOOL_ACCESS_STATUS.PENDING]:         's-pending',
  [TOOL_ACCESS_STATUS.RENEWAL_PENDING]: 's-renewal',
  [TOOL_ACCESS_STATUS.REVOKE_PENDING]:  's-revoke',
  [TOOL_ACCESS_STATUS.EXPIRED]:         's-expired',
  [TOOL_ACCESS_STATUS.REVOKED]:         's-revoked',
  [TOOL_ACCESS_STATUS.DECLINED]:        's-declined',
};

export const TOOL_ACCESS_EVENT_DOT_COLOR: Record<string, string> = {
  ACCESS_REQUESTED:     '#2196f3',
  ACCESS_RE_REQUESTED:  '#2196f3',
  ACCESS_GRANTED:       '#12b76a',
  ACCESS_RE_GRANTED:    '#12b76a',
  ACCESS_RENEWED:       '#12b76a',
  ACCESS_EXPIRED:       '#667085',
  ACCESS_EXPIRING_SOON: '#F1B10C',
  REVOKE_REQUESTED:     '#2196f3',
  ACCESS_REVOKED:       '#f04438',
  RENEWAL_REQUESTED:    '#2196f3',
  RENEWAL_DECLINED:     '#667085',
  REVOKE_DECLINED:      '#2196f3',
  ACCESS_DECLINED:      '#f04438',
  UPGRADE_REQUESTED:    '#2196f3',
  UPGRADE_GRANTED:      '#12b76a',
  UPGRADE_DECLINED:     '#667085',
};

export const TOOL_ACCESS_EVENT_TYPE_CLASS: Record<string, string> = {
  ACCESS_REQUESTED:     'al-et-info',
  ACCESS_RE_REQUESTED:  'al-et-info',
  ACCESS_GRANTED:       'al-et-success',
  ACCESS_RE_GRANTED:    'al-et-success',
  ACCESS_RENEWED:       'al-et-success',
  ACCESS_EXPIRED:       'al-et-neutral',
  ACCESS_EXPIRING_SOON: 'al-et-warn',
  REVOKE_REQUESTED:     'al-et-info',
  ACCESS_REVOKED:       'al-et-danger',
  RENEWAL_REQUESTED:    'al-et-info',
  RENEWAL_DECLINED:     'al-et-neutral',
  REVOKE_DECLINED:      'al-et-info',
  ACCESS_DECLINED:      'al-et-danger',
  UPGRADE_REQUESTED:    'al-et-info',
  UPGRADE_GRANTED:      'al-et-success',
  UPGRADE_DECLINED:     'al-et-neutral',
};

export const TOOL_ACCESS_EVENT_LABELS: Record<string, string> = {
  ACCESS_REQUESTED:     'Access Requested',
  ACCESS_RE_REQUESTED:  'Access Re-Requested',
  ACCESS_GRANTED:       'Access Granted',
  ACCESS_RE_GRANTED:    'Access Re-Granted',
  ACCESS_RENEWED:       'Access Renewed',
  ACCESS_DECLINED:      'Access Declined',
  RENEWAL_REQUESTED:    'Renewal Requested',
  RENEWAL_DECLINED:     'Renewal Declined',
  REVOKE_REQUESTED:     'Revoke Requested',
  REVOKE_DECLINED:      'Revoke Declined',
  ACCESS_REVOKED:       'Access Revoked',
  ACCESS_EXPIRED:       'Access Expired',
  ACCESS_EXPIRING_SOON: 'Access Expiring Soon',
  UPGRADE_REQUESTED:    'Upgrade Requested',
  UPGRADE_GRANTED:      'Upgrade Granted',
  UPGRADE_DECLINED:     'Upgrade Declined',
};
export const TOOL_ACCESS_EVENT = {
  ACCESS_REQUESTED: "ACCESS_REQUESTED",
  ACCESS_RE_REQUESTED: "ACCESS_RE_REQUESTED",
  ACCESS_GRANTED: "ACCESS_GRANTED",
  ACCESS_RE_GRANTED: "ACCESS_RE_GRANTED",
  ACCESS_RENEWED: "ACCESS_RENEWED",
  ACCESS_REVOKED: "ACCESS_REVOKED",
  ACCESS_DECLINED: "ACCESS_DECLINED",
  ACCESS_EXPIRED: "ACCESS_EXPIRED",
  ACCESS_EXPIRING_SOON: "ACCESS_EXPIRING_SOON",
  RENEWAL_REQUESTED: "RENEWAL_REQUESTED",
  RENEWAL_DECLINED: "RENEWAL_DECLINED",
  REVOKE_REQUESTED: "REVOKE_REQUESTED",
  REVOKE_DECLINED: "REVOKE_DECLINED",
  UPGRADE_REQUESTED: "UPGRADE_REQUESTED",
  UPGRADE_GRANTED: "UPGRADE_GRANTED",
  UPGRADE_DECLINED: "UPGRADE_DECLINED",
} as const;
